/**
 * What every surface that covers the page shares (a Drawer, the Lightbox): the rest of the app made inert behind it and Tab
 * kept inside it. Moved here from screens/useDrawers.tsx (AIC-503) so components don't import from a screen.
 */

/** Controls Tab can reach inside an element. */
const TABBABLE = "a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";
const tabbables = (root: HTMLElement) => [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter((el) => !el.closest("dialog") && !el.closest("[inert]"));

/** The part of a keyboard event these helpers read: a React synthetic event and a native one both fit. */
interface KeyLike {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly defaultPrevented: boolean;
  readonly target: EventTarget | null;
  preventDefault(): void;
}

/**
 * For a modal panel's keydown: Tab and Shift+Tab go round inside `panel`. A lightbox opened from it keeps its own. From
 * the panel's heading or the panel itself (tabindex -1, before the first control), Shift+Tab goes round to the last.
 */
export function keepTabInside(e: KeyLike, panel: HTMLElement | null, also: readonly HTMLElement[] = []): void {
  if (!panel || e.key !== "Tab" || e.defaultPrevented || (e.target as HTMLElement).closest("dialog")) return;
  const list = [...tabbables(panel), ...also.flatMap(tabbables)];
  if (list.length === 0) return;
  const first = list[0]!;
  const last = list.at(-1)!;
  const at = document.activeElement as HTMLElement | null;
  const listed = !!at && list.includes(at);
  if (e.shiftKey && (at === first || !listed)) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
}

/**
 * What stays live behind a modal surface: the toasts (announced, and their Try again reachable) and the app's aria-live
 * announcers (the toasts' own, the next step's). They hold no page controls of their own.
 */
const STAYS_LIVE = ".ipd-toasts, [aria-live]";

/**
 * Makes everything but `keep` inert: each sibling of `keep` and of each of its ancestors up to <body>, except what
 * STAYS_LIVE: a sibling that is one is left alone, and one that holds one is gone into, its other children made inert.
 * `live` adds to that for one surface (the Results evidence sheet keeps the call player bar usable, AIC-503).
 * Returns the undo. Elements already inert are left as they are.
 */
export function inertAround(keep: HTMLElement, live = ""): () => void {
  const changed: HTMLElement[] = [];
  const stays = live ? `${STAYS_LIVE}, ${live}` : STAYS_LIVE;
  const mark = (el: Element) => {
    if (!(el instanceof HTMLElement) || el.hasAttribute("inert") || el.matches(stays)) return;
    if (el.querySelector(stays)) { for (const child of el.children) mark(child); return; }
    el.setAttribute("inert", "");
    changed.push(el);
  };
  for (let el: HTMLElement | null = keep; el && el !== document.body; el = el.parentElement) {
    for (const sib of el.parentElement?.children ?? []) if (sib !== el) mark(sib);
  }
  return () => { for (const el of changed) el.removeAttribute("inert"); };
}
